/**
 * Post-hoc track corrections — WITHOUT mutating the raw recorded points.
 *
 * The recorded track (feature.geometry.coordinates + parallel `coord*` arrays)
 * is treated as immutable. Gap fills are stored as an OVERLAY
 * (`properties.gapFills`) and merged into the track only when assembling the
 * cleaned/display track (`assembleTrack`). The "Full" view shows the raw track
 * exactly as recorded; "Cleaned" shows raw + fills.
 *
 * Split is the one structural edit (it produces two activities); it partitions
 * the real recorded points (no fabrication) by timestamp.
 */
import { featureStorage } from '../../storage/feature-storage.js'
import { activityStorage } from '../../storage/activity-storage.js'
import { db } from '../../database/schema.js'
import { authService } from '../auth/auth-service.js'
import { autoAssociateActivity } from '../plan-association-service.js'
import { elevationPointService } from '../terrain/elevation-point-service.js'
import { lineString, point } from '@turf/helpers'
import { lineSlice } from '@turf/line-slice'
import { nearestPointOnLine } from '@turf/nearest-point-on-line'
import type { Feature } from '../../schemas/feature.js'
import { haversineMeters, type TrackGap } from './track-gaps.js'
import { logger } from '../../utils/logger.js'

const log = logger.create('track-edit')

/** A stored gap fill: synthetic points inserted AFTER raw index `afterRawIndex`
 *  (i.e. across the gap between it and the next raw point). */
export interface GapFill {
  afterRawIndex: number
  coords: number[][]
  times: number[]
  demElevations: (number | null)[]
}

interface RawArrays {
  coords: number[][]
  coordTimes: number[]
  coordAccuracies: (number | null)[]
  coordActivities: (string | null)[]
  coordSpeeds: (number | null)[]
  coordDemElevations: (number | null)[]
  /** Legacy only: older fills wrote points straight into the geometry. */
  coordInterpolated: boolean[]
}

/** Read the raw parallel arrays off a feature. */
function readArrays(feature: Feature): RawArrays {
  const coords = ((feature.geometry as GeoJSON.LineString).coordinates ?? []) as number[][]
  const p = (feature.properties ?? {}) as Record<string, unknown>
  const n = coords.length
  const col = <T>(key: string, dflt: T): T[] => {
    const v = p[key]
    return Array.isArray(v) && v.length === n ? (v as T[]) : coords.map(() => dflt)
  }
  return {
    coords,
    coordTimes: col<number>('coordTimes', 0),
    coordAccuracies: col<number | null>('coordAccuracies', null),
    coordActivities: col<string | null>('coordActivities', null),
    coordSpeeds: col<number | null>('coordSpeeds', null),
    coordDemElevations: col<number | null>('coordDemElevations', null),
    coordInterpolated: col<boolean>('coordInterpolated', false),
  }
}

function readGapFills(feature: Feature): GapFill[] {
  const v = (feature.properties as { gapFills?: unknown } | undefined)?.gapFills
  return Array.isArray(v) ? (v as GapFill[]) : []
}

/** Raw-index → true if a gap starting at that index has been filled. */
export function filledGapStartIndices(feature: Feature): Set<number> {
  return new Set(readGapFills(feature).map(f => f.afterRawIndex))
}

export interface AssembledTrack {
  coords: number[][]
  coordTimes: number[]
  coordAccuracies: (number | null)[]
  coordActivities: (string | null)[]
  coordSpeeds: (number | null)[]
  coordDemElevations: (number | null)[]
  interpolated: boolean[]
}

/**
 * Merge raw points with gap-fill overlays into a single track for processing.
 * Raw points keep interpolated=false; fill points are inserted after their
 * `afterRawIndex` with interpolated=true. The raw feature is never modified.
 */
export function assembleTrack(feature: Feature): AssembledTrack {
  const a = readArrays(feature)
  const fillsByIndex = new Map<number, GapFill>()
  for (const f of readGapFills(feature)) fillsByIndex.set(f.afterRawIndex, f)

  const out: AssembledTrack = {
    coords: [],
    coordTimes: [],
    coordAccuracies: [],
    coordActivities: [],
    coordSpeeds: [],
    coordDemElevations: [],
    interpolated: [],
  }
  for (let i = 0; i < a.coords.length; i++) {
    out.coords.push(a.coords[i])
    out.coordTimes.push(a.coordTimes[i])
    out.coordAccuracies.push(a.coordAccuracies[i])
    out.coordActivities.push(a.coordActivities[i])
    out.coordSpeeds.push(a.coordSpeeds[i])
    out.coordDemElevations.push(a.coordDemElevations[i])
    out.interpolated.push(false)

    const fill = fillsByIndex.get(i)
    if (fill) {
      for (let k = 0; k < fill.coords.length; k++) {
        out.coords.push(fill.coords[k])
        out.coordTimes.push(fill.times[k])
        out.coordAccuracies.push(null)
        out.coordActivities.push(null)
        out.coordSpeeds.push(null)
        out.coordDemElevations.push(fill.demElevations[k] ?? null)
        out.interpolated.push(true)
      }
    }
  }
  return out
}

// ─── Slope-aware fill timing ────────────────────────────────────────────────

const lerp = (a: number, b: number, f: number): number => a + (b - a) * f

function toblerPace(slope: number): number {
  const kmh = 6 * Math.exp(-3.5 * Math.abs(slope + 0.05))
  const mps = (kmh * 1000) / 3600
  return mps > 0 ? 1 / mps : 1
}

const slopeBucket = (slope: number): number => Math.round(slope / 0.05)

/** Slope→pace (s/m) model built from the track's own moving segments. */
function buildSlopePaceModel(a: RawArrays): (slope: number) => number {
  const elevAt = (i: number): number | null =>
    a.coordDemElevations[i] ?? (a.coords[i].length >= 3 ? a.coords[i][2] : null)
  const bins = new Map<number, { dist: number; time: number }>()
  let totDist = 0
  let totTime = 0
  for (let i = 1; i < a.coords.length; i++) {
    if (a.coordInterpolated[i] || a.coordInterpolated[i - 1]) continue
    const d = haversineMeters(a.coords[i - 1], a.coords[i])
    const dt = (a.coordTimes[i] - a.coordTimes[i - 1]) / 1000
    if (d < 1 || dt <= 0 || d > 250) continue
    const e0 = elevAt(i - 1)
    const e1 = elevAt(i)
    if (e0 == null || e1 == null) continue
    const b = slopeBucket((e1 - e0) / d)
    const cur = bins.get(b) ?? { dist: 0, time: 0 }
    cur.dist += d
    cur.time += dt
    bins.set(b, cur)
    totDist += d
    totTime += dt
  }
  const overall = totDist > 0 ? totTime / totDist : null
  return (slope: number): number => {
    const hit = bins.get(slopeBucket(slope))
    if (hit && hit.dist > 0) return hit.time / hit.dist
    return overall ?? toblerPace(slope)
  }
}

function straightFillCoords(from: number[], to: number[]): number[][] {
  const steps = Math.max(1, Math.min(60, Math.round(haversineMeters(from, to) / 50)))
  const coords: number[][] = []
  for (let k = 1; k < steps; k++) {
    const f = k / steps
    coords.push([lerp(from[0], to[0], f), lerp(from[1], to[1], f)])
  }
  return coords
}

/**
 * Interior fill coordinates following the plan route between the gap endpoints.
 * The route may be several segments; pick the one BOTH endpoints lie on (within
 * ~200 m), then slice it. Falls back to a straight line.
 */
function routeFillCoords(from: number[], to: number[], lines: number[][][]): number[][] {
  let best: number[][] | null = null
  let bestScore = Infinity
  for (const line of lines) {
    if (line.length < 2) continue
    const ls = lineString(line)
    const df = nearestPointOnLine(ls, point(from)).properties.dist
    const dt = nearestPointOnLine(ls, point(to)).properties.dist
    const score = Math.max(df, dt) // km — both endpoints must be on this segment
    if (score < bestScore) {
      bestScore = score
      best = line
    }
  }
  if (!best || bestScore > 0.2) return straightFillCoords(from, to)
  try {
    const sliced = lineSlice(point(from), point(to), lineString(best)).geometry
      .coordinates as number[][]
    const interior = sliced.slice(1, -1)
    return interior.length > 0 ? interior : straightFillCoords(from, to)
  } catch {
    return straightFillCoords(from, to)
  }
}

function fillTimes(
  path: number[][],
  elevs: (number | null)[],
  t0: number,
  t1: number,
  pace: (slope: number) => number
): number[] {
  const seg: number[] = []
  let totalRaw = 0
  for (let i = 1; i < path.length; i++) {
    const d = haversineMeters(path[i - 1], path[i])
    const e0 = elevs[i - 1]
    const e1 = elevs[i]
    const slope = e0 != null && e1 != null && d > 0 ? (e1 - e0) / d : 0
    const raw = d * pace(slope)
    seg.push(raw)
    totalRaw += raw
  }
  const scale = totalRaw > 0 ? (t1 - t0) / totalRaw : 0
  const times: number[] = []
  let acc = 0
  for (let m = 1; m <= path.length - 2; m++) {
    acc += seg[m - 1]
    times.push(Math.round(t0 + scale * acc))
  }
  return times
}

/**
 * Fill a gap by storing a gap-fill OVERLAY (the raw geometry is untouched).
 * `routeLines` (the plan's route segments) makes the fill follow the trail;
 * otherwise it's a straight line. Timed with the track's own slope-aware pace.
 */
export async function fillGap(
  feature: Feature,
  gap: TrackGap,
  routeLines?: number[][][]
): Promise<void> {
  const a = readArrays(feature)
  const from = a.coords[gap.startIdx]
  const to = a.coords[gap.endIdx]
  const t0 = a.coordTimes[gap.startIdx]
  const t1 = a.coordTimes[gap.endIdx]

  const interior =
    routeLines && routeLines.length > 0
      ? routeFillCoords(from, to, routeLines)
      : straightFillCoords(from, to)
  if (interior.length === 0) return

  const elevAt = (i: number): number | null =>
    a.coordDemElevations[i] ?? (a.coords[i].length >= 3 ? a.coords[i][2] : null)
  const interiorElevs = await Promise.all(
    interior.map(c => elevationPointService.getElevation(c[1], c[0]))
  )
  const path = [from, ...interior, to]
  const elevs = [elevAt(gap.startIdx), ...interiorElevs, elevAt(gap.endIdx)]
  const times = fillTimes(path, elevs, t0, t1, buildSlopePaceModel(a))

  const fill: GapFill = {
    afterRawIndex: gap.startIdx,
    coords: interior,
    times,
    demElevations: interiorElevs,
  }
  const gapFills = [...readGapFills(feature).filter(f => f.afterRawIndex !== gap.startIdx), fill]
  await featureStorage.updateFeature(feature.id, {
    properties: { ...(feature.properties ?? {}), gapFills },
  })
  log.debug(`Stored gap fill overlay (${interior.length} points), raw track untouched`)
}

/** Remove the gap-fill overlay for a gap (revert). */
export async function clearGapFill(feature: Feature, afterRawIndex: number): Promise<void> {
  const gapFills = readGapFills(feature).filter(f => f.afterRawIndex !== afterRawIndex)
  await featureStorage.updateFeature(feature.id, {
    properties: { ...(feature.properties ?? {}), gapFills },
  })
}

/**
 * Migrate legacy data: older fills inserted interpolated points directly into
 * the geometry (flagged `coordInterpolated`). Strip those back out to restore
 * the raw recording, and drop the flag. Returns true if anything changed.
 */
export async function stripLegacyInterpolation(feature: Feature): Promise<boolean> {
  const a = readArrays(feature)
  if (!a.coordInterpolated.some(Boolean)) return false
  const keep = <T>(arr: T[]): T[] => arr.filter((_, i) => !a.coordInterpolated[i])
  const props = { ...(feature.properties ?? {}) } as Record<string, unknown>
  delete props.coordInterpolated
  props.coordTimes = keep(a.coordTimes)
  props.coordAccuracies = keep(a.coordAccuracies)
  props.coordActivities = keep(a.coordActivities)
  props.coordSpeeds = keep(a.coordSpeeds)
  props.coordDemElevations = keep(a.coordDemElevations)
  await featureStorage.updateFeature(feature.id, {
    geometry: { type: 'LineString', coordinates: keep(a.coords) },
    properties: props,
  })
  log.debug('Stripped legacy in-geometry interpolated points; raw recording restored')
  return true
}

/**
 * Split a track into two activities at `splitTimeMs`. The first activity keeps
 * points at/before the time; a new activity gets the rest. Partitions only the
 * REAL recorded points (gap-fill overlays are dropped — re-fillable). Re-runs
 * plan association on both halves.
 */
export async function splitTrack(
  activityId: string,
  feature: Feature,
  splitTimeMs: number
): Promise<{ newActivityId: string }> {
  const a = readArrays(feature)
  const splitIdx = a.coordTimes.findIndex(t => t > splitTimeMs)
  if (splitIdx < 2 || splitIdx > a.coords.length - 2) {
    throw new Error('Split point must leave at least 2 points on each side')
  }
  const head = <T>(arr: T[]): T[] => arr.slice(0, splitIdx)
  const tail = <T>(arr: T[]): T[] => arr.slice(splitIdx)

  const createdBy = authService.getCurrentUserId()
  const orig = await db.activities.get(activityId)
  const activityType = orig?.activityType ?? 'hike'

  const tailTimes = tail(a.coordTimes)
  const created = await activityStorage.createActivity({
    activityType,
    startTime: new Date(tailTimes[0]).toISOString(),
    endTime: new Date(tailTimes[tailTimes.length - 1]).toISOString(),
    origin: { type: 'user-created' },
    createdBy,
  })
  if (!created.success || !created.data) {
    throw new Error(`Failed to create split activity: ${created.error?.message}`)
  }
  const newActivityId = created.data.id
  await featureStorage.createFeature(
    {
      name: 'Track',
      type: 'track',
      geometry: { type: 'LineString', coordinates: tail(a.coords) },
      properties: {
        coordTimes: tail(a.coordTimes),
        coordAccuracies: tail(a.coordAccuracies),
        coordActivities: tail(a.coordActivities),
        coordSpeeds: tail(a.coordSpeeds),
        coordDemElevations: tail(a.coordDemElevations),
      },
      tags: ['track'],
      createdBy,
    },
    { entityType: 'activity', entityId: newActivityId }
  )

  // Trim the original to the head (drop any fill overlay — re-fillable).
  const headProps = { ...(feature.properties ?? {}) } as Record<string, unknown>
  delete headProps.gapFills
  delete headProps.gapsAcknowledged
  headProps.coordTimes = head(a.coordTimes)
  headProps.coordAccuracies = head(a.coordAccuracies)
  headProps.coordActivities = head(a.coordActivities)
  headProps.coordSpeeds = head(a.coordSpeeds)
  headProps.coordDemElevations = head(a.coordDemElevations)
  await featureStorage.updateFeature(feature.id, {
    geometry: { type: 'LineString', coordinates: head(a.coords) },
    properties: headProps,
  })
  await activityStorage.updateActivity(activityId, {
    endTime: new Date(a.coordTimes[splitIdx - 1]).toISOString(),
  })

  try {
    await autoAssociateActivity(activityId, head(a.coords), a.coordTimes[0])
    await autoAssociateActivity(newActivityId, tail(a.coords), tailTimes[0])
  } catch (e) {
    log.warn('Re-association after split failed:', e)
  }

  return { newActivityId }
}
